import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { Form, Label, Button, Select } from '@shared/components';
import { getFieldErrors, isApiError } from '@/services/api';
import {
    customerNameChangeSchema,
    type CustomerNameChangeFormInputs,
} from '../../schemas/customerNameChange.schema';
import { computeCustomerDiff, type DiffEntry } from '../EditCustomerForm/computeCustomerDiff';
import { EditCustomerConfirmModal } from '../EditCustomerForm/EditCustomerConfirmModal';
import { CustomerNameFields } from '../fields/CustomerNameFields';
import { useChangeCustomerNameMutation } from '../../hooks/useChangeCustomerNameMutation';
import {
    evidenceTypeLabel,
    nameChangeReasonLabel,
    requesterTypeLabel,
    toOptions,
} from '../../utils/amendmentLabels';
import {
    EVIDENCE_TYPES,
    NAME_CHANGE_REASONS,
    REQUESTER_TYPES,
    type Customer,
} from '../../types/customers.types';

interface Props {
    customer: Customer;
    agreementId: string;
    onCancel: () => void;
    onSaved: () => void;
}

type FormField = keyof CustomerNameChangeFormInputs;

const WIRE_TO_FORM: Record<string, FormField> = {
    title: 'title',
    forename: 'forename',
    surname: 'surname',
    reason: 'reason',
    requester_type: 'requesterType',
    evidence_type: 'evidenceType',
};

const AMENDMENT_FIELDS: Array<{
    name: 'reason' | 'requesterType' | 'evidenceType';
    id: string;
    label: string;
    placeholder: string;
    options: Array<{ value: string; label: string }>;
}> = [
    {
        name: 'reason',
        id: 'nameChangeReason',
        label: 'Reason for the change',
        placeholder: 'Select a reason',
        options: toOptions(NAME_CHANGE_REASONS, nameChangeReasonLabel),
    },
    {
        name: 'requesterType',
        id: 'nameChangeRequester',
        label: 'Who requested the change',
        placeholder: 'Select who asked',
        options: toOptions(REQUESTER_TYPES, requesterTypeLabel),
    },
    {
        name: 'evidenceType',
        id: 'nameChangeEvidence',
        label: 'Evidence seen',
        placeholder: 'Select the evidence seen',
        options: toOptions(EVIDENCE_TYPES, evidenceTypeLabel),
    },
];

const UNCHANGED_MESSAGE = 'The name is unchanged. Change the title, forename or surname before continuing.';

export const HolderNameChangeForm: React.FC<Props> = ({
    customer,
    agreementId,
    onCancel,
    onSaved,
}) => {
    const [pending, setPending] = useState<{ values: CustomerNameChangeFormInputs; diff: DiffEntry[] } | null>(
        null,
    );
    const [submitError, setSubmitError] = useState<string | null>(null);

    const mutation = useChangeCustomerNameMutation(customer.id!);

    const { control, handleSubmit, setError, formState } = useForm<CustomerNameChangeFormInputs>({
        resolver: zodResolver(customerNameChangeSchema),
        defaultValues: {
            title: customer.title ?? '',
            forename: customer.forename ?? '',
            surname: customer.surname ?? '',
        },
        mode: 'onBlur',
    });

    const handleReviewChanges: SubmitHandler<CustomerNameChangeFormInputs> = (values) => {
        const diff = computeCustomerDiff(customer, {
            title: values.title,
            forename: values.forename,
            surname: values.surname,
        });

        // The server refuses an unchanged name; say so before a round trip.
        if (diff.length === 0) {
            setSubmitError(UNCHANGED_MESSAGE);
            return;
        }

        setSubmitError(null);
        setPending({ values, diff });
    };

    const handleConfirm = async () => {
        if (!pending) return;
        setSubmitError(null);

        try {
            await mutation.mutateAsync({ ...pending.values, agreementId });
            setPending(null);
            onSaved();
        } catch (error) {
            const fieldErrors = Object.entries(getFieldErrors(error));

            fieldErrors.forEach(([wireField, messages]) => {
                const formField = WIRE_TO_FORM[wireField];
                if (formField) setError(formField, { message: messages[0] });
            });

            setSubmitError(
                fieldErrors.length > 0
                    ? 'Some fields could not be saved. Check the highlighted fields.'
                    : isApiError(error) && error.code === 'name_unchanged'
                      ? UNCHANGED_MESSAGE
                      : 'The name change could not be saved. Your entries have been kept — try again.',
            );
            setPending(null);
        }
    };

    const handleCancelConfirm = () => {
        if (mutation.isPending) return;
        setPending(null);
    };

    return (
        <>
            <Form onSubmit={handleSubmit(handleReviewChanges)} className="space-y-4">
                <CustomerNameFields control={control} stacked />

                {AMENDMENT_FIELDS.map(({ name, id, label, placeholder, options }) => (
                    <div key={name}>
                        <Label htmlFor={id}>{label} *</Label>
                        <Controller
                            name={name}
                            control={control}
                            render={({ field, fieldState }) => (
                                <Select
                                    {...field}
                                    id={id}
                                    options={options}
                                    placeholder={placeholder}
                                    ariaLabel={label}
                                    value={field.value ?? ''}
                                    error={!!fieldState.error}
                                    hint={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                ))}

                <p className="text-xs text-mp-muted">
                    The evidence is recorded, not stored. Do not upload or attach a document.
                </p>

                {submitError && (
                    <p className="text-sm text-mp-error" role="alert">
                        {submitError}
                    </p>
                )}

                <div className="mt-4 flex justify-end gap-2">
                    <Button onClick={onCancel} variant="clear" type="button">
                        Cancel
                    </Button>
                    <Button type="submit" disabled={formState.isSubmitting}>
                        Review changes
                    </Button>
                </div>
            </Form>

            <EditCustomerConfirmModal
                isOpen={pending !== null}
                diff={pending?.diff ?? []}
                isSubmitting={mutation.isPending}
                heading="Confirm the holder's new name"
                notice={
                    pending && (
                        <p className="mb-4 text-sm text-mp-muted">
                            Reason for the change: {nameChangeReasonLabel(pending.values.reason)}
                        </p>
                    )
                }
                onCancel={handleCancelConfirm}
                onConfirm={handleConfirm}
            />
        </>
    );
};
